<template>
  <div class="ptos">
    <page-heading title="PTOs"></page-heading>

    <content-placeholder v-if="fetching">
      <el-card>
        <p class="text text-10 text-h-20"></p>
        <p class="text text-full"></p>
        <p class="text text-full"></p>
        <p class="text text-50"></p>
        <p class="text text-25"></p>
      </el-card>
    </content-placeholder>

    <div v-else>
      <el-card>
        <div slot="header">
          <span>Pending PTOs</span>
        </div>
        <div slot="footer">
          Total: {{ pendingTotal }}
        </div>
        <ptos-list :ptos="pendingPtos" :default-sort="{ prop: 'starts_at', order: 'ascending' }"></ptos-list>
        <div class="pagination" v-if="pendingTotal > pendingPageSize">
          <el-pagination
            @current-change="handlePendingPageChange"
            :current-page.sync="pendingPage"
            :page-size="pendingPageSize"
            layout="total, prev, pager, next"
            :total="pendingTotal">
          </el-pagination>
        </div>
      </el-card>
      <el-card v-if="fetchingAll">
        <p class="text text-10 text-h-20"></p>
        <p class="text text-full"></p>
        <p class="text text-full"></p>
        <p class="text text-50"></p>
        <p class="text text-25"></p>
      </el-card>
      <el-card v-else>
        <el-tabs v-model="activeName">
          <div class="between">
            <el-input type="text" v-model.lazy="filter" class="ptos_name_search" :placeholder="$t('modules.ptoAvailability.fields.search.placeholder')"></el-input>
            <el-date-picker type="year" class="yearPicker" v-model.lazy.trim="year"  format="yyyy" :picker-options="{format:'yyyy'}" :placeholder="$t('modules.ptoAvailability.fields.search.year')"></el-date-picker>
            <el-select
              v-model="selectedEmployeeId"
              class="employeePicker"
              placeholder="Filter by Employee"
              filterable
              clearable>
              <el-option
                v-for="employee in employees"
                :key="employee.id"
                :label="employee.profile.full_name"
                :value="employee.id">
              </el-option>
            </el-select>
          </div>
          <el-tab-pane :label="`Rejected PTOs`" name="second">
            <el-card>
              <div slot="header">
                <span>Rejected PTOs</span>
              </div>
              <div slot="footer">
                Total: {{ rejectedTotal }}
              </div>
              <ptos-list :ptos="rejectedPtos"></ptos-list>
              <div class="pagination" v-if="rejectedTotal > rejectedPageSize">
                <el-pagination
                  @current-change="handleRejectedPageChange"
                  :current-page.sync="rejectedPage"
                  :page-size="rejectedPageSize"
                  layout="total, prev, pager, next"
                  :total="rejectedTotal">
                </el-pagination>
              </div>
            </el-card>
          </el-tab-pane>
          <el-tab-pane :label="`Approved PTOs`" name="third">
            <el-card>
              <div slot="header">
                <span>Approved PTOs</span>
              </div>
              <div slot="footer">
                Total: {{ approvedTotal }}
              </div>
              <ptos-list :ptos="approvedPtos"></ptos-list>
              <div class="pagination" v-if="approvedTotal > approvedPageSize">
                <el-pagination
                  @current-change="handleApprovedPageChange"
                  :current-page.sync="approvedPage"
                  :page-size="approvedPageSize"
                  layout="total, prev, pager, next"
                  :total="approvedTotal">
                </el-pagination>
              </div>
            </el-card>
          </el-tab-pane>
        </el-tabs>
      </el-card>
      <pto-form-modal />
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';

  import PtosList from '@/components/pto/PtosList';
  import PtoFormModal from '@/components/pto/PtoFormModal';

  export default {
    name: 'PtosPage',
    components: {
      ptoFormModal: PtoFormModal,
      ptosList: PtosList,
    },
    data() {
      return {
        activeName: 'third',
        translationPrefix: 'pages.ptoRequests',
        filter: '',
        year: '',
        selectedEmployeeId: null,
        pendingPtos: [],
        rejectedPtos: [],
        approvedPtos: [],
        rejectedPtosAll: null,
        fetchingAll: true,
        pendingPage: 1,
        pendingPageSize: 20,
        pendingTotal: 0,
        rejectedPage: 1,
        rejectedPageSize: 20,
        rejectedTotal: 0,
        approvedPage: 1,
        approvedPageSize: 20,
        approvedTotal: 0,
        approvedPtosAll: null,
      };
    },
    created() {
      this.fetchPendingPtos();
      this.fetchAllPtos();
      this.fetchEmployees();
    },
    watch: {
      activeName() {
        this.filter = '';
        this.year = '';
        this.selectedEmployeeId = null;
      },
      filter() {
        this.rejectedPage = 1;
        this.approvedPage = 1;
        this.rejectedPtosAll = null;
        this.approvedPtosAll = null;
        this.fetchAllPtos();
      },
      year() {
        this.rejectedPage = 1;
        this.approvedPage = 1;
        this.rejectedPtosAll = null;
        this.approvedPtosAll = null;
        this.fetchAllPtos();
      },
      selectedEmployeeId() {
        this.rejectedPage = 1;
        this.approvedPage = 1;
        this.rejectedPtosAll = null;
        this.approvedPtosAll = null;
        this.fetchAllPtos();
      },
    },

    computed: {
      ...mapGetters('entities/ptos', ['fetching']),
      ...mapGetters('entities/users', ['currentUser']),
      employees() {
        return this.$store.getters['entities/users/query']()
          .where(record => !record.deactivated)
          .with('profile')
          .get()
          .filter(record => record.profile && record.profile.full_name)
          .sort((a, b) => a.profile.full_name.localeCompare(b.profile.full_name));
      },
    },
    methods: {
      ...mapActions('entities/ptos', ['fetchPtos', 'openFormModal']),
      ...mapActions('entities/users', ['fetchUsers']),

      fetchEmployees() {
        return this.fetchUsers({ per_page: 1000 });
      },

      normalizePtosResponse(response) {
        const payload = response && response.data ? response.data : {};
        const data = payload.data || payload;

        return {
          entities: data.entities || [],
          meta: data.meta || {},
        };
      },

      getPtoYear(pto) {
        const date = pto.starts_at || pto.created_at;
        return new Date(date).getFullYear();
      },

      currentYear() {
        return new Date().getFullYear();
      },

      sortPtosByDateDesc(ptos) {
        return ptos.sort((a, b) => {
          const aDate = new Date(a.starts_at || a.created_at);
          const bDate = new Date(b.starts_at || b.created_at);
          return bDate - aDate;
        });
      },

      paginate(arr, page, pageSize) {
        const start = (page - 1) * pageSize;
        return arr.slice(start, start + pageSize);
      },

      getFilteredPtos(ptos) {
        let filtered = ptos.slice();

        if (this.filter.length > 0) {
          filtered = filtered.filter(data =>
            data.user && data.user.profile
            && data.user.profile.full_name.toLowerCase().includes(this.filter.toLowerCase()),
          );
        }

        const year = new Date(this.year).getFullYear();
        if (year && year !== 1970) {
          filtered = filtered.filter(data => this.getPtoYear(data) === year);
        }

        if (this.selectedEmployeeId) {
          filtered = filtered.filter(data => data.user_id === this.selectedEmployeeId);
        }

        return filtered;
      },

      buildApprovedFetchParams(page = this.approvedPage, perPage = this.approvedPageSize) {
        const params = {
          page,
          per_page: perPage,
          status: 'approved',
          order: 'desc',
        };

        if (this.selectedEmployeeId) {
          params.user_id = this.selectedEmployeeId;
        }

        return params;
      },

      buildRejectedParams() {
        const params = {
          page: this.rejectedPage,
          per_page: this.rejectedPageSize,
          status: 'rejected',
          order: 'desc',
        };

        if (this.selectedEmployeeId) {
          params.user_id = this.selectedEmployeeId;
        }

        return params;
      },

      buildApprovedParams() {
        return this.buildApprovedFetchParams(this.approvedPage, this.approvedPageSize);
      },

      buildPendingParams() {
        const params = {
          page: this.pendingPage,
          per_page: this.pendingPageSize,
        };
        return params;
      },

      fetchAllPtos() {
        this.fetchingAll = true;
        Promise.all([
          this.fetchRejectedPtos(),
          this.fetchApprovedPtos(),
        ]).finally(() => {
          this.fetchingAll = false;
        });
      },

      fetchPendingPtos() {
        return this.fetchPtos({ ...this.buildPendingParams(), status: 'pending' })
          .then((response) => {
            if (response.status) {
              const { entities, meta } = this.normalizePtosResponse(response);
              this.pendingPtos = entities;
              this.pendingTotal = meta.total_entries || 0;
            }
          });
      },

      fetchRejectedPtos() {
        if (this.selectedEmployeeId) {
          return this.fetchPtos({
            status: 'rejected',
            order: 'desc',
            page: 1,
            per_page: 200,
            user_id: this.selectedEmployeeId,
          }).then((response) => {
            if (response.status) {
              const { entities } = this.normalizePtosResponse(response);
              this.rejectedPtosAll = this.sortPtosByDateDesc(entities);
              const filtered = this.getFilteredPtos(this.rejectedPtosAll);
              this.rejectedTotal = filtered.length;
              this.rejectedPtos = this.paginate(filtered, this.rejectedPage, this.rejectedPageSize);
            }
          });
        }

        this.rejectedPtosAll = null;
        return this.fetchPtos(this.buildRejectedParams())
          .then((response) => {
            if (response.status) {
              const { entities, meta } = this.normalizePtosResponse(response);
              this.rejectedPtos = entities.sort((a, b) =>
                new Date(b.created_at) - new Date(a.created_at),
              );
              this.rejectedTotal = meta.total_entries || 0;
              this.applyFilters();
            }
          });
      },

      fetchApprovedPtos() {
        if (this.selectedEmployeeId) {
          return this.fetchPtos({
            status: 'approved',
            order: 'desc',
            page: 1,
            per_page: 200,
            user_id: this.selectedEmployeeId,
          }).then((response) => {
            if (response.status) {
              const { entities } = this.normalizePtosResponse(response);
              this.approvedPtosAll = this.sortPtosByDateDesc(entities);
              const filtered = this.getFilteredPtos(this.approvedPtosAll);
              this.approvedTotal = filtered.length;
              this.approvedPtos = this.paginate(filtered, this.approvedPage, this.approvedPageSize);
            }

            return Promise.resolve();
          });
        }

        if (this.approvedPtosAll && this.filter.length === 0 && !this.year && !this.selectedEmployeeId) {
          this.approvedTotal = this.approvedPtosAll.length;
          this.approvedPtos = this.paginate(this.approvedPtosAll, this.approvedPage, this.approvedPageSize);
          return Promise.resolve();
        }

        return this.fetchPtos(this.buildApprovedParams())
          .then((response) => {
            if (response.status) {
              const { entities, meta } = this.normalizePtosResponse(response);
              this.approvedPtos = this.sortPtosByDateDesc(entities);
              this.approvedTotal = meta.total_entries || 0;

              const hasCurrentYear = this.approvedPtos.some(data => this.getPtoYear(data) === this.currentYear());
              const canFallbackToAllPages = this.filter.length === 0
                && !this.year
                && !this.selectedEmployeeId
                && this.approvedPage === 1
                && this.approvedTotal > this.approvedPtos.length
                && !hasCurrentYear;

              if (canFallbackToAllPages) {
                return this.fetchAllApprovedPtos();
              }

              this.applyFilters();
            }

            return Promise.resolve();
          });
      },

      fetchAllApprovedPtos() {
        const perPage = 200;
        const firstPage = this.fetchPtos(this.buildApprovedFetchParams(1, perPage));

        return firstPage.then((response) => {
          if (!response.status) {
            return Promise.resolve();
          }

          const { entities, meta } = this.normalizePtosResponse(response);
          const totalEntries = meta.total_entries || entities.length;
          const totalPages = Math.max(1, Math.ceil(totalEntries / perPage));

          if (totalPages === 1) {
            this.approvedPtosAll = this.sortPtosByDateDesc(entities.slice());
            this.approvedTotal = this.approvedPtosAll.length;
            this.approvedPtos = this.paginate(this.approvedPtosAll, this.approvedPage, this.approvedPageSize);
            this.applyFilters();
            return Promise.resolve();
          }

          // Avoid hammering every page (some deep pages intermittently return 500).
          // Probe a few strategic pages: early pages + last page.
          const pagesToFetch = [2, 3, totalPages]
            .filter(page => page > 1 && page <= totalPages)
            .filter((page, index, arr) => arr.indexOf(page) === index);

          const requests = pagesToFetch.map(page => this.fetchPtos(this.buildApprovedFetchParams(page, perPage)));

          return Promise.all(requests).then((responses) => {
            const all = entities.slice();

            responses.forEach((res) => {
              if (res && res.status) {
                const normalized = this.normalizePtosResponse(res);
                all.push(...normalized.entities);
              }
            });

            this.approvedPtosAll = this.sortPtosByDateDesc(all);
            this.approvedTotal = this.approvedPtosAll.length;
            this.approvedPtos = this.paginate(this.approvedPtosAll, this.approvedPage, this.approvedPageSize);
            this.applyFilters();
          });
        });
      },

      applyFilters() {
        // Apply client-side filtering for name and year
        if (this.filter.length > 0) {
          this.rejectedPtos = this.rejectedPtos.filter(data =>
            data.user && data.user.profile &&
            data.user.profile.full_name.toLowerCase().includes(this.filter.toLowerCase()),
          );
          this.approvedPtos = this.approvedPtos.filter(data =>
            data.user && data.user.profile &&
            data.user.profile.full_name.toLowerCase().includes(this.filter.toLowerCase()),
          );
        }

        const year = new Date(this.year).getFullYear();
        if (year && year !== 1970) {
          this.rejectedPtos = this.rejectedPtos.filter(data =>
            this.getPtoYear(data) === year,
          );
          this.approvedPtos = this.approvedPtos.filter(data =>
            this.getPtoYear(data) === year,
          );
        }

        if (this.selectedEmployeeId) {
          this.rejectedPtos = this.rejectedPtos.filter(data => data.user_id === this.selectedEmployeeId);
          this.approvedPtos = this.approvedPtos.filter(data => data.user_id === this.selectedEmployeeId);
        }

        // Re-sort after filtering to maintain newest first order
        this.rejectedPtos.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        this.approvedPtos.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      },

      handlePendingPageChange(val) {
        this.pendingPage = val;
        this.fetchPendingPtos();
      },

      handleRejectedPageChange(val) {
        this.rejectedPage = val;

        if (this.selectedEmployeeId && this.rejectedPtosAll) {
          const filtered = this.getFilteredPtos(this.rejectedPtosAll);
          this.rejectedPtos = this.paginate(filtered, this.rejectedPage, this.rejectedPageSize);
          this.rejectedTotal = filtered.length;
          return;
        }

        this.fetchRejectedPtos();
      },

      handleApprovedPageChange(val) {
        this.approvedPage = val;

        if (this.selectedEmployeeId && this.approvedPtosAll) {
          const filtered = this.getFilteredPtos(this.approvedPtosAll);
          this.approvedPtos = this.paginate(filtered, this.approvedPage, this.approvedPageSize);
          this.approvedTotal = filtered.length;
          return;
        }

        if (this.approvedPtosAll && this.filter.length === 0 && !this.year && !this.selectedEmployeeId) {
          this.approvedPtos = this.paginate(this.approvedPtosAll, this.approvedPage, this.approvedPageSize);
          return;
        }

        this.fetchApprovedPtos();
      },
    },
  };
</script>

<style lang="scss">
  .el-tabs__content{
    overflow: visible;
  }

  .ptos_name_search {
    max-width: 300px;
    width: 300px;
    margin-right: 10px;
  }

  .between{
    display: flex;
    justify-content: left;
    align-items: center;
    margin-bottom: 10px;
  }

  .yearPicker{
    width: auto !important;
    margin-right: 10px;
  }

  .employeePicker{
    width: 260px;
  }

  .pagination {
    margin-top: 20px;
    text-align: center;
  }
</style>



// WEBPACK FOOTER //
// src/pages/Ptos.vue